import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Loader2 } from "lucide-react";

/* THE ONE PLACE AN ACCESS-REMOVING ACTION IS CONFIRMED.
 *
 * Born out of 2026-10-02: the owner pressed Reset 2FA on his own account, it fired, and he was
 * signed out. The detail-page button did ask first (a native `confirm`), but the KEY ICON ON THE
 * USERS LIST asked nothing at all — `act(u, "reset-totp", …)` went straight to the API, on any row
 * including your own. His ruling: *"Two buttons doing the same thing with different safety is
 * exactly how this happened. Make them one implementation."*
 *
 * The rules this enforces:
 *   · YOUR OWN account needs a DELIBERATE act — type RESET. A native confirm's OK button has
 *     keyboard focus, so one stray Enter or a second click lands on it; typing a word cannot
 *     happen by accident.
 *   · SOMEBODY ELSE'S account needs a confirmation that NAMES THEM, so a mis-aimed click is
 *     obvious before it fires ("Deactivate Dave?" — his example).
 *   · Cancel is always free. Unlike OneTimeReveal, closing this box loses nothing, so Escape and
 *     a click outside are allowed to mean "no".
 *   · The WORDING lives here too, not at the call sites — the builders below are the single
 *     source, so the two pages cannot drift apart again.
 *
 * NOT confirmed here, deliberately: Activate and Restore. They GIVE access back, so a mistaken
 * click does no harm and is one press to reverse — *"a confirm there is friction for nothing."*
 */
export function ConfirmAction({ pending, onClose, testid = "confirm-action" }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const word = pending?.typeToConfirm || "";
  const armed = !word || typed.trim().toUpperCase() === word;

  const close = () => { if (busy) return; setTyped(""); onClose(); };
  const go = async () => {
    if (!armed || busy) return;
    setBusy(true);
    try { await pending.run(); }
    finally { setBusy(false); setTyped(""); onClose(); }
  };

  return (
    <Dialog open={!!pending} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="rounded-lg border-slate-200 sm:max-w-md" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-[#C76714] shrink-0 mt-0.5" />
            <span data-testid={`${testid}-title`}>{pending?.title}</span>
          </DialogTitle>
        </DialogHeader>
        <ul className="space-y-1.5 text-sm text-slate-700" data-testid={`${testid}-lines`}>
          {(pending?.lines || []).map((l, i) => (
            <li key={i} className="flex gap-2"><span className="text-[#C76714] shrink-0">•</span><span>{l}</span></li>
          ))}
        </ul>
        {word && (
          <label className="block rounded-lg border border-[#B01E28]/40 bg-red-50 p-3">
            <span className="block text-xs text-[#8F1820] leading-snug mb-2" data-testid={`${testid}-type-prompt`}>
              This is <b>your own account</b>. Type <b className="font-mono">{word}</b> to confirm you mean it.
            </span>
            <Input data-testid={`${testid}-type-input`} value={typed} autoComplete="off" spellCheck={false}
              onChange={(e) => setTyped(e.target.value)} placeholder={word}
              className="rounded-lg border-slate-300 font-mono tracking-widest" />
          </label>
        )}
        <DialogFooter>
          <Button variant="outline" data-testid={`${testid}-cancel`} disabled={busy} onClick={close}
            className="rounded-lg border-slate-200">Cancel</Button>
          <Button data-testid={`${testid}-confirm`} disabled={!armed || busy} onClick={go}
            className="rounded-lg bg-[#B01E28] hover:bg-[#8F1820] text-white">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
            {pending?.confirmLabel || "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const who = (u) => (u?.name ? `${u.name} (${u.email})` : u?.email);

/* ── THE WORDING, IN ONE PLACE ─────────────────────────────────────────────────────────────── */

export function resetTotpConfirm(u, isSelf, run) {
  return isSelf
    ? {
      title: "Reset YOUR OWN 2FA?",
      lines: [
        "You'll be signed out immediately.",
        "You'll need your PASSWORD to sign back in — make sure you know it before continuing.",
        "You'll scan a new QR code with your authenticator app.",
        "Your printed backup codes keep working.",
      ],
      confirmLabel: "Reset my 2FA",
      typeToConfirm: "RESET",
      run,
    }
    : {
      title: `Reset 2FA for ${who(u)}?`,
      lines: [
        "They're signed out immediately.",
        "They'll need their PASSWORD to sign back in.",
        "They'll scan a new QR code at next login.",
        "Their printed backup codes keep working.",
      ],
      confirmLabel: "Reset their 2FA",
      run,
    };
}

export function deactivateConfirm(u, run) {
  return {
    title: `Deactivate ${who(u)}?`,
    lines: [
      "They're signed out immediately and cannot log in until reactivated.",
      "Nothing they recorded is removed or hidden.",
      "One press of Activate puts them back.",
    ],
    confirmLabel: "Deactivate",
    run,
  };
}

export function archiveConfirm(u, run) {
  return {
    title: `Archive ${who(u)}?`,
    lines: [
      "They're hidden from the Users list and their login is disabled.",
      "Their history stays exactly as it is — nothing is deleted.",
      "Show archived → Restore brings them back, un-archived and reactivated.",
    ],
    confirmLabel: "Archive",
    run,
  };
}
